import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { postAPI } from '../api';

// 게시물 인터페이스
export interface Post {
  post_id: number;
  title: string;
  content?: string;
  author: {
    id: number;
    username: string;
  };
  createdAt: string;
  updatedAt?: string;
  board: {
    id: number;
    name: string;
  };
  commentCount?: number;
  viewCount?: number;
  isPopular?: boolean;
}

export interface PaginationState {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

// 게시판 인터페이스
export interface Board {
  id: number;
  name: string;
  description: string;
}

interface PostContextType {
  posts: {[boardId: number] : {posts: Post[], pagination: PaginationState}};
  list: {[boardId: number] : {list: Post[], pagination: PaginationState}};
  allPosts: {posts: Post[], pagination: PaginationState};
  loading: boolean;
  error: string | null;
  getList: (boardId: number) => {list: Post[], pagination: PaginationState};
  getPosts: (boardId: number) => {posts: Post[], pagination: PaginationState};
  fetchPosts: (page?: number, limit?: number) => Promise<void>;
  fetchPostsByBoard: (boardId: number, page?: number, limit?: number) => Promise<void>;
  fetchPostById: (postId: number) => Promise<Post | null>;
  clearError: () => void;
  fetchPostListByBoard: (boardId: number, page?: number, limit?: number, searchType?:number, searchText?:string) => Promise<void>;
}

const PostContext = createContext<PostContextType | undefined>(undefined);

interface PostProviderProps {
  children: ReactNode;
}

export const PostProvider: React.FC<PostProviderProps> = ({ children }) => {
  const [posts, setPosts] = useState<{[boardId: number] : {posts: Post[], pagination: PaginationState}}>({});
  const [list, setList] = useState<{[boardId: number] : {list: Post[], pagination: PaginationState}}>({});
  const [allPosts, setAllPosts] = useState<{posts: Post[], pagination: PaginationState}>({posts: [], pagination: {currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 20}});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // 모든 게시물 가져오기
  const fetchPosts = useCallback(async (page: number = 1, limit: number = 10) => {
    try {
      setLoading(true);
      setError(null);
      const response = await postAPI.getPosts(page, limit);
      const postsData = response.data.posts || response.data;

      setAllPosts({
        posts: postsData,
        pagination: response.data.pagination
      });

    } catch (err: any) {
      const errorMessage = err.response?.data?.message || '게시물을 불러오는 중 오류가 발생했습니다.';
      setError(errorMessage);
      console.error('게시물 로딩 오류:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 게시판별 게시물 가져오기
  const fetchPostsByBoard = useCallback(async (boardId: number, page: number = 1, limit: number = 10) => {
    try {
      setLoading(true);
      setError(null);
      const response = await postAPI.getPosts(page, limit, undefined, boardId);
      const postsData = response.data.posts || response.data;
      setPosts(prev=>{
        return {
          ...prev,
          [boardId]: {
            posts: postsData,
            pagination: response.data.pagination
          }
        }
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || '게시물을 불러오는 중 오류가 발생했습니다.';
      setError(errorMessage);
      console.error('게시물 로딩 오류:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPostListByBoard = useCallback(async (boardId: number, page: number = 1, limit: number = 10, searchType:number=0, searchText:string="") => {
    try {
      setLoading(true);
      setError(null);
      const response = await postAPI.getPostList(page, limit, searchText, searchType, boardId);
      const postsData = response.data.posts || response.data;
      setList(prev=>{
        return {
          ...prev,
          [boardId]: {
            list: postsData,
            pagination: response.data.pagination
          }
        }
      });

    } catch (err: any) {
      const errorMessage = err.response?.data?.message || '게시물을 불러오는 중 오류가 발생했습니다.';
      setError(errorMessage);
      console.error('게시물 로딩 오류:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // 특정 게시물 가져오기
  const fetchPostById = useCallback(async (postId: number): Promise<Post | null> => {
    try {
      setLoading(true);
      setError(null);
      const response = await postAPI.getPostById(postId);
      const postData = response.data.post || response.data;
      return postData;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || '게시물을 불러오는 중 오류가 발생했습니다.';
      setError(errorMessage);
      console.error('게시물 로딩 오류:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);
  
  // 에러 메시지 초기화
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  
  const getList = useCallback((boardId: number) => {
    if(list[boardId]){
      return list[boardId];
    }
    return {list: [], pagination: {currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 20}};
  }, [list]);

  const getPosts = useCallback((boardId: number) => {
    if(posts[boardId]){
      return posts[boardId];
    }
    return {posts: [], pagination: {currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 20}};
  }, [posts]);

  // 컨텍스트 값 설정
  const value = {
    posts,
    list,
    allPosts,
    loading,
    error,
    getList,
    getPosts,
    fetchPosts,
    fetchPostsByBoard,
    fetchPostById,
    clearError,
    fetchPostListByBoard
  };

  return (
    <PostContext.Provider value={value}>
      {children}
    </PostContext.Provider>
  );
};

// 게시물 컨텍스트 사용을 위한 커스텀 훅
export const usePost = (): PostContextType => {
  const context = useContext(PostContext);
  if (context === undefined) {
    throw new Error('usePost는 PostProvider 내부에서만 사용할 수 있습니다.');
  }
  return context;
}; 